// Les écritures hors ligne (lot 6.5) : les gestes simples partent toujours avec un identifiant ; si le réseau manque, ils
// attendent dans une file (IndexedDB, sur cet appareil) et repartent avec le MÊME identifiant au retour du réseau. Le cœur
// le reconnaît : même si la première réponse s'est perdue, l'action n'est jamais faite deux fois. En attendant, l'écran
// montre le geste comme fait (`appliquerAEtat`, `appliquerAuBrief`), pour qu'on ne le refasse pas.

import type { CorpsMarche, CorpsSeance, Etat, ItemAgenda } from '@alfred/shared';

export interface ActionEnFile {
  /** Choisi avant le premier essai : c'est lui qui évite les doublons (X-Alfred-Action). */
  id: string;
  /** Une file n'appartient qu'à un compte : elle est vidée si un autre se connecte. */
  membreId: string;
  genre: 'rappel-fait' | 'rappel-phrase' | 'seance' | 'marche';
  chemin: string;
  corps: unknown;
  /** Ce que la personne reconnaît, si l'action est refusée au retour du réseau. */
  libelle: string;
  /** ISO : au-delà de 14 jours, une action qui n'arrive pas à partir est abandonnée. */
  creeLe: string;
}

/** Ce qu'on fait d'une action selon la réponse : partie, à retenter plus tard, ou refusée pour de bon. */
export function decisionEnvoi(statut: number): 'partie' | 'garder' | 'refusee' {
  if (statut >= 200 && statut < 300) return 'partie';
  // Session expirée, notice à accepter, action en cours, trop de requêtes, serveur en panne : ça passera.
  if ([401, 403, 408, 409, 425, 429].includes(statut) || statut >= 500) return 'garder';
  return 'refusee';
}

const AGE_MAX_MS = 14 * 86_400_000;

export interface BilanEnvoi {
  parties: string[];
  refusees: { id: string; libelle: string; raison: string }[];
  /** Le réseau manque encore : on s'est arrêté là. */
  horsLigne: boolean;
}

/**
 * Envoie les actions dans l'ordre, une à une : un rappel créé puis coché doit le rester dans cet ordre. `envoyer` rend
 * le statut HTTP et la raison d'un refus, ou lève une erreur si le réseau manque : on s'arrête alors là.
 */
export async function traiterFile(
  file: ActionEnFile[],
  envoyer: (a: ActionEnFile) => Promise<{ statut: number; raison?: string }>,
  maintenant = Date.now(),
): Promise<BilanEnvoi> {
  const bilan: BilanEnvoi = { parties: [], refusees: [], horsLigne: false };
  for (const a of file) {
    let r;
    try {
      r = await envoyer(a);
    } catch {
      bilan.horsLigne = true;
      break;
    }
    const d = decisionEnvoi(r.statut);
    if (d === 'partie') bilan.parties.push(a.id);
    else if (d === 'refusee') bilan.refusees.push({ id: a.id, libelle: a.libelle, raison: r.raison ?? `erreur ${r.statut}` });
    else if (maintenant - new Date(a.creeLe).getTime() > AGE_MAX_MS) bilan.refusees.push({ id: a.id, libelle: a.libelle, raison: 'trop ancienne, elle n’a jamais pu partir' });
  }
  return bilan;
}

// --- ce que l'écran montre en attendant ----------------------------------------------------------------------------------

/** Les rappels cochés en attente, sous la forme « id|jour ». */
function cochesDe(file: ActionEnFile[]): Set<string> {
  const coches = new Set<string>();
  for (const a of file) {
    if (a.genre !== 'rappel-fait') continue;
    const id = /^\/api\/rappels\/([^/]+)\/fait$/.exec(a.chemin)?.[1];
    if (id) coches.add(`${id}|${(a.corps as { jour: string }).jour}`);
  }
  return coches;
}

const garde = (coches: Set<string>) => (i: ItemAgenda) => !(i.rappelId && coches.has(`${i.rappelId}|${i.jour}`));

/** Un brief (ou les alertes de l'état) sans les rappels déjà cochés dans la file. */
export function appliquerAuBrief<T extends { aujourdhui: ItemAgenda[]; enRetard: ItemAgenda[]; aVenir: ItemAgenda[] }>(brief: T, file: ActionEnFile[]): T {
  const coches = cochesDe(file);
  if (!coches.size) return brief;
  const g = garde(coches);
  return { ...brief, aujourdhui: brief.aujourdhui.filter(g), enRetard: brief.enRetard.filter(g), aVenir: brief.aVenir.filter(g) };
}

/** L'état du jour, avec les gestes de la file déjà comptés. */
export function appliquerAEtat(etat: Etat, file: ActionEnFile[]): Etat {
  if (!file.length) return etat;
  const coches = cochesDe(file);
  const g = garde(coches);
  let e: Etat = {
    ...etat,
    alertes: appliquerAuBrief(etat.alertes, file),
    portails: coches.size ? etat.portails.map((j) => ({ ...j, items: j.items.filter(g) })).filter((j) => j.items.length) : etat.portails,
  };
  for (const a of file) {
    if (a.genre === 'marche') {
      const c = a.corps as CorpsMarche;
      if (c.date !== e.date) continue;
      e = {
        ...e,
        quotidien: e.quotidien.map((q) => {
          if (q.id === 'tapis' && c.tapisAjoutMin) return { ...q, minutes: q.minutes + c.tapisAjoutMin };
          if (q.id !== 'tapis' && c.ecole) return { ...q, minutes: q.objectifMin };
          return q;
        }),
      };
    } else if (a.genre === 'seance' && e.seance && !e.seance.faite) {
      const c = a.corps as CorpsSeance;
      if (c.date !== e.seance.date) continue;
      e = {
        ...e,
        seance: { ...e.seance, faite: { activite: `Séance ${c.lettre}`, duree: `${c.dureeMin ?? e.seance.seance.duree_min} min`, ressenti: c.ressenti, annulable: null } },
        semaine: { ...e.semaine, faites: e.semaine.faites + 1 },
      };
    }
  }
  return e;
}

// --- le stockage -------------------------------------------------------------------------------------------------------

const BASE = 'alfred-file';
const MAGASIN = 'actions';

function ouvrir(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const r = indexedDB.open(BASE, 1);
    // Les identifiants sont choisis dans l'ordre du temps : l'ordre de la clé est celui des gestes.
    r.onupgradeneeded = () => r.result.createObjectStore(MAGASIN, { keyPath: 'id' });
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error ?? new Error('file indisponible'));
  });
}

async function transaction<T>(mode: IDBTransactionMode, faire: (m: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await ouvrir();
  try {
    return await new Promise<T>((resolve, reject) => {
      const t = db.transaction(MAGASIN, mode);
      const r = faire(t.objectStore(MAGASIN));
      t.oncomplete = () => resolve(r.result);
      t.onerror = () => reject(t.error ?? new Error('file indisponible'));
      t.onabort = () => reject(t.error ?? new Error('file indisponible'));
    });
  } finally {
    db.close();
  }
}

/** Sans IndexedDB (navigation privée stricte), la file est vide : le geste échoue en le disant, comme avant le lot 6.5. */
export async function lireFile(): Promise<ActionEnFile[]> {
  try {
    return await transaction('readonly', (m) => m.getAll() as IDBRequest<ActionEnFile[]>);
  } catch {
    return [];
  }
}

export async function mettreEnFile(a: ActionEnFile): Promise<void> {
  await transaction('readwrite', (m) => m.put(a));
}

export async function retirerDeFile(id: string): Promise<void> {
  await transaction('readwrite', (m) => m.delete(id)).catch(() => undefined);
}

export async function viderFile(): Promise<void> {
  await transaction('readwrite', (m) => m.clear()).catch(() => undefined);
}

/** Un identifiant croissant dans le temps (l'ordre de la file), et unique : la date, puis du hasard. */
export function nouvelIdAction(maintenant = Date.now()): string {
  const hasard = Array.from(crypto.getRandomValues(new Uint8Array(12)), (o) => o.toString(16).padStart(2, '0')).join('');
  return `${maintenant.toString(36).padStart(10, '0')}-${hasard}`;
}
